import React, { useEffect, useRef, useState } from "react";
import { withRouter } from "tools/routerCompat";
import { connect } from "react-redux";
import { UserInformation, LoginActivity, EmployeeDetails, TeamMemberManagement } from "components/User";
import QAMacCard from "components/User/Mac/QAMacCard";
import { MSpinner, MCard } from "components/Shared";
import MGridLayout, { MGridLayoutItem } from "components/Shared/MGridLayout";
import { BUSOPS_AGENT_MAC_BONUS_SHOW, MANAGEMENT_CALENDAR_EDIT, QA_TEAM_MAINTENANCE, EMPLOYEE_COURSE_VIEW, AUTHENTICATION_EDIT } from 'typeConstants/rights';
import { userHasRight } from "selectors/rights";
import EmployeeCalendar from "components/Employee/EmployeeCalendar";
import { getEmployeeDetails } from "requests/employee";
import EmployeeTEAM from "components/Employee/EmployeeTEAM";
import EmployeeCourses from "components/Employee/EmployeeCourses";
import EmployeeMappings from "components/EmployeeCustomerSurveyTypes/EmployeeMappings";
import { InternetUser, EmployeeDetailDto, InternetUserLoginStatisticsDto } from "interfaces/Mueller.Business.Models";
import { auditQAQueueTypeMSelectOption, homeownersQaQueueTypeMSelectOption } from "typeConstants/employeeCustomerSurveyTypes";

interface AgentProfileProps {
  verticalDisplay: boolean,
  loginStatistics: InternetUserLoginStatisticsDto,
  getLoginStatistics: (internetUserId: number) => InternetUserLoginStatisticsDto,
  isFetchingLoginStatistics: boolean,
  internetUserId: number,
  internetUser: InternetUser,
  getUser: (internetUserId: number) => InternetUser,
  isFetchingInternetUser: boolean,
  isAddingTimeClock: boolean,
  isDeletingTimeClock: boolean,
  canEditCalendarAsManager: boolean,
  showTeamManagementCard: boolean,
  canViewEmployeeCourses: boolean,
  canViewMacBonus: boolean,
  canEditUsers: boolean,
  supervisorName: string,
}

const AgentProfile = (props: AgentProfileProps) => {

  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [internetUserId, setInternetUserId] = useState(0);

  const mounted = useRef(true);

  useEffect(() => {
    let userId = props.internetUserId;
    setInternetUserId(userId);
    props.getLoginStatistics(userId);
    props.getUser(userId);
    
    getEmployeeDetails(userId, (response: EmployeeDetailDto) => setEmployeeDetails(response), mounted);
  }, [props.internetUserId]);

  useEffect(() => () => { mounted.current = false; }, []);

  let isLoading = props.isFetchingLoginStatistics ||
    props.isFetchingInternetUser ||
    props.loginStatistics == null ||
    Object.keys(props.internetUser).length === 0 ||
    employeeDetails == null;

  if (isLoading) return (<MSpinner />);

  const rows: MGridLayoutItem[][] = [
    // Row 1: Employee Details, User Information, Login Activity
    [
      { component: <EmployeeDetails internetUserId={internetUserId} />, size: 4 },
      { component: <UserInformation userInformation={props.internetUser} internetUserId={internetUserId} canEditUser={props.canEditUsers} />, size: 4 },
      { component: <LoginActivity loginStatistics={props.loginStatistics} />, size: 4 },
    ],
    // Row 2: Employee TEAM, Team Member Management (conditional), Employee Mappings
    [
      { component: <EmployeeTEAM internetUserId={internetUserId} overrideCellWidth />, size: 6 },
      ...(props.showTeamManagementCard ? [{ component: <TeamMemberManagement internetUserId={internetUserId} supervisorName={props.supervisorName} />, size: 3 }] : []),
      { component: <EmployeeMappings employeeId={employeeDetails.employeeId} overrideQueueTypes={[homeownersQaQueueTypeMSelectOption ,auditQAQueueTypeMSelectOption]} />, size: 3 },
    ],
    // Row 3: Employee Calendar
    [
      {
        component: (
          <MCard
            title="Employee Calendar"
            fields={[{
              key: "calendar",
              control: (
                <EmployeeCalendar
                  employeeId={employeeDetails.employeeId}
                  readOnly={props.canEditCalendarAsManager ? false : true}
                  allowManagementEdit={props.canEditCalendarAsManager}
                />)
            }]}
          />
        ),
        size: 12,
      },
    ],
    // Row 4: Employee Courses (conditional)
    ...(props.canViewEmployeeCourses ? [[{ component: <EmployeeCourses employeeId={employeeDetails.employeeId} />, size: 12 }]] : []),
    // Row 5: MAC Bonus (conditional)
    ...(props.canViewMacBonus ? [[{ component: <QAMacCard internetUserId={internetUserId} />, size: 12 }]] : []),
  ];

  return (
    <MGridLayout
      verticalDisplay={props.verticalDisplay}
      rows={rows}
      padding={props.verticalDisplay ? 0 : 1}
    />
  );
};

const mapStateToProps = state => {
  return {
    canEditCalendarAsManager: userHasRight(MANAGEMENT_CALENDAR_EDIT)(state),
    showTeamManagementCard: userHasRight(QA_TEAM_MAINTENANCE)(state),
    canViewEmployeeCourses: userHasRight(EMPLOYEE_COURSE_VIEW)(state),
    canViewMacBonus: userHasRight(BUSOPS_AGENT_MAC_BONUS_SHOW)(state),
    canEditUsers: userHasRight(AUTHENTICATION_EDIT)(state),
    isFetchingInternetUser: state.user.isFetchingInternetUser,
    isFetchingLoginStatistics: state.user.isFetchingLoginStatistics,
    loginStatistics: state.user.loginStatistics,
    internetUser: state.user.internetUser,
  };
};

export default withRouter(connect(mapStateToProps)(AgentProfile));
